import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, FileText, StickyNote, ListTodo, PhoneCall, Repeat, Zap } from "lucide-react";

const TYPE_META = {
  note: { label: "Note", icon: StickyNote, cls: "bg-[#FEF3C7] text-[#92400E]" },
  task: { label: "Task", icon: ListTodo, cls: "bg-[#E0E7FF] text-[#3730A3]" },
  call: { label: "Call", icon: PhoneCall, cls: "bg-[#DCFCE7] text-[#166534]" },
  status_change: { label: "Handoff / status", icon: Repeat, cls: "bg-[#E5E7EB] text-[#374151]" },
  enrollment: { label: "Enrollment link", icon: Zap, cls: "bg-[#FCE7F3] text-[#9D174D]" },
  system: { label: "System", icon: FileText, cls: "bg-[#F1F5F9] text-[#475569]" },
};

export default function HelmWorkLog() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  const load = async () => {
    setData(null);
    const { data } = await api.get("/agent/worklog", { params: { q, type, limit: 200 } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  return (
    <div className="p-6 sm:p-10 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight" data-testid="worklog-title">Work Log</h1>
        <p className="text-muted-foreground mt-1.5">
          {data?.scope === "all" ? "Every logged action across all households in the agency." : "Every logged action across your assigned households."}
          {" "}Real notes, tasks, handoffs, and enrollment-link events — nothing fabricated.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search the log…" className="pl-9 h-11 bg-white" data-testid="worklog-search" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="h-11 rounded-md border bg-white px-2 text-sm" data-testid="worklog-type-filter">
          <option value="">All types</option>
          {(data?.types || []).map((t) => <option key={t} value={t}>{TYPE_META[t]?.label || t}</option>)}
        </select>
      </div>

      {!data ? <Skeleton className="h-72 w-full rounded-xl" /> : data.items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-[#E5E5E0] bg-white p-10 text-center text-[#6B7280]" data-testid="worklog-empty">No matching activity.</div>
      ) : (
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white divide-y" data-testid="worklog-list">
          {data.items.map((it) => {
            const m = TYPE_META[it.type] || TYPE_META.system;
            const Icon = m.icon;
            return (
              <button key={it.id} onClick={() => it.household_id && navigate(`/agent/households/${it.household_id}`)}
                className="w-full text-left flex items-start gap-3 p-3 hover:bg-[#FBFBF9]" data-testid={`worklog-item-${it.id}`}>
                <span className={`mt-0.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded shrink-0 ${m.cls}`}><Icon className="w-3 h-3" />{m.label}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#0B192C]">{it.title}</p>
                  <p className="text-xs text-[#9CA3AF]">{it.household_name} · {(it.created_at || "").slice(0, 10)}{it.actor_role ? ` · ${it.actor_role}` : ""}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
